/**
 * UTM Lead Tracking Script
 */
(function () {
  'use strict';

  const CONFIG = {
    UTM_PARAMETERS: ['gclid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign'],
    LEAD_PARAMETERS: ['gclid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'http_referer'],
    COOKIE_NAME: 'Lead',
    EMPTY_PARAM_VALUE: '',
    EMPTY_REFERER_VALUE: 'empty',
    HIDDEN_FIELD_SELECTORS: '.always-hidden, .form-field-hidden',
    JOTFORM_CLASS: 'jotform-form'
  };

  const processedForms = new WeakSet();

  function isDependencyAvailable(globalName) {
    return typeof window[globalName] !== 'undefined' && window[globalName] !== null;
  }

  function safeParseJSON(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.warn('Failed to parse JSON:', error.message);
      return null;
    }
  }

  function encodeCookieValue(value) {
    return encodeURIComponent(value);
  }

  function decodeCookieValue(value) {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  function getCookie(name) {
    if (isDependencyAvailable('Cookies')) {
      return Cookies.get(name);
    }

    const encodedName = encodeCookieValue(name) + '=';
    const cookieValue = document.cookie
      .split('; ')
      .find((cookie) => cookie.indexOf(encodedName) === 0);

    return cookieValue ? decodeCookieValue(cookieValue.slice(encodedName.length)) : undefined;
  }

  function setCookie(name, value, options = {}) {
    if (isDependencyAvailable('Cookies')) {
      try {
        Cookies.set(name, value, options);
        return true;
      } catch (error) {
        console.warn('Failed to set cookie:', error.message);
        return false;
      }
    }

    document.cookie = `${encodeCookieValue(name)}=${encodeCookieValue(value)}; path=/; SameSite=Lax`;
    return true;
  }

  function removeCookie(name) {
    if (isDependencyAvailable('Cookies')) {
      try {
        Cookies.remove(name);
        return true;
      } catch (error) {
        console.warn('Failed to remove cookie:', error.message);
        return false;
      }
    }

    document.cookie = `${encodeCookieValue(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    return true;
  }

  function getUrlParameters() {
    try {
      return Object.fromEntries(new URLSearchParams(window.location.search));
    } catch (error) {
      console.warn('Failed to parse URL parameters:', error.message);
      return {};
    }
  }

  function getTrackingUrlParameters(urlParams) {
    const trackingParams = {};

    CONFIG.UTM_PARAMETERS.forEach((param) => {
      if (Object.prototype.hasOwnProperty.call(urlParams, param)) {
        trackingParams[param] = urlParams[param] || CONFIG.EMPTY_PARAM_VALUE;
      }
    });

    return trackingParams;
  }

  function hasTrackingParameters(urlParams) {
    return CONFIG.UTM_PARAMETERS.some((param) => Object.prototype.hasOwnProperty.call(urlParams, param));
  }

  function getHttpReferer() {
    try {
      return document.referrer || CONFIG.EMPTY_REFERER_VALUE;
    } catch (error) {
      console.warn('Failed to get document referrer:', error.message);
      return CONFIG.EMPTY_REFERER_VALUE;
    }
  }

  function isExternalReferer(refererUrl) {
    if (!refererUrl || refererUrl === CONFIG.EMPTY_REFERER_VALUE) {
      return false;
    }

    try {
      return new URL(refererUrl).hostname !== window.location.hostname;
    } catch (error) {
      return true;
    }
  }

  function getBestReferer(existingReferer) {
    const currentReferer = getHttpReferer();

    if (isExternalReferer(currentReferer)) {
      return currentReferer;
    }

    return existingReferer || CONFIG.EMPTY_REFERER_VALUE;
  }

  function createLeadData(urlParams, existingReferer = null) {
    return {
      parameters: {
        ...getTrackingUrlParameters(urlParams),
        http_referer: getBestReferer(existingReferer)
      }
    };
  }

  function getExistingLeadData() {
    const cookieValue = getCookie(CONFIG.COOKIE_NAME);

    if (!cookieValue) {
      return null;
    }

    return safeParseJSON(cookieValue);
  }

  function saveLeadData(leadData) {
    return setCookie(CONFIG.COOKIE_NAME, JSON.stringify(leadData), {});
  }

  function mergeExistingLeadData(existingLead) {
    const parameters = existingLead && existingLead.parameters ? { ...existingLead.parameters } : {};
    const currentReferer = getHttpReferer();

    if (isExternalReferer(currentReferer)) {
      parameters.http_referer = currentReferer;
    } else if (!Object.prototype.hasOwnProperty.call(parameters, 'http_referer')) {
      parameters.http_referer = CONFIG.EMPTY_REFERER_VALUE;
    }

    return { parameters };
  }

  function determineLeadAction(urlParams, existingLead) {
    const hasExistingLead = existingLead !== null;

    if (hasTrackingParameters(urlParams)) {
      return {
        action: hasExistingLead ? 'replace' : 'create',
        data: createLeadData(urlParams, existingLead && existingLead.parameters && existingLead.parameters.http_referer)
      };
    }

    if (hasExistingLead) {
      return {
        action: 'use_existing',
        data: mergeExistingLeadData(existingLead)
      };
    }

    return {
      action: 'create',
      data: createLeadData({})
    };
  }

  function populateFormFields(doc, leadData) {
    if (!leadData || !leadData.parameters) {
      return;
    }

    CONFIG.LEAD_PARAMETERS.forEach((param) => {
      const value = leadData.parameters[param];
      const selector = `input[name*='${param}']`;

      try {
        doc.querySelectorAll(selector).forEach((field) => {
          field.value = value || CONFIG.EMPTY_PARAM_VALUE;
        });
      } catch (error) {
        console.warn(`Failed to populate field ${param}:`, error.message);
      }
    });
  }

  function pushToDataLayer(data) {
    if (typeof window.dataLayer !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(data);
    }
  }

  function processFormData(formData) {
    const processedData = {};

    for (const [key, value] of formData) {
      let processedKey = key;

      if (key.startsWith('q')) {
        const match = key.match(/^q\d+_(.*)$/);
        if (match) {
          processedKey = match[1];
        }
      }

      processedData[processedKey] = value;
    }

    return processedData;
  }

  function hasEmptyRequiredField(form) {
    return Array.from(form.elements).some((element) => {
      if (typeof element.closest === 'function' && element.closest(CONFIG.HIDDEN_FIELD_SELECTORS)) {
        return false;
      }

      if (!element.required || element.disabled || element.type === 'hidden') {
        return false;
      }

      return !String(element.value || '').trim();
    });
  }

  function hasTrackingFields(form) {
    return CONFIG.LEAD_PARAMETERS.some((param) => {
      try {
        return Boolean(form.querySelector(`input[name*='${param}']`));
      } catch (error) {
        return false;
      }
    });
  }

  function isJotForm(form) {
    return form.classList.contains(CONFIG.JOTFORM_CLASS);
  }

  function isShopifyForm(form) {
    return Boolean(
      form.querySelector('[name^="contact["], [name^="customer["], [name="form_type"], [data-type="add-to-cart-form"]')
    );
  }

  function shouldPreventDefault(form) {
    if (isJotForm(form) || isShopifyForm(form)) {
      return false;
    }

    return hasTrackingFields(form);
  }

  function nativeSubmit(form) {
    if (typeof HTMLFormElement !== 'undefined' && HTMLFormElement.prototype.submit) {
      HTMLFormElement.prototype.submit.call(form);
      return;
    }

    form.submit();
  }

  function pushFormDataToDataLayer(form) {
    const formData = new FormData(form);
    const processedData = processFormData(formData);

    Object.entries(processedData).forEach(([key, value]) => {
      pushToDataLayer({ [key]: value });
    });

    pushToDataLayer({ event_id: Date.now().toString() });
  }

  function handleFormSubmission(form) {
    if (processedForms.has(form)) {
      return;
    }

    if (!hasTrackingFields(form) && !isJotForm(form)) {
      return;
    }

    processedForms.add(form);

    form.addEventListener('submit', function (event) {
      if (hasEmptyRequiredField(form)) {
        return;
      }

      try {
        pushFormDataToDataLayer(form);
      } catch (error) {
        console.warn('Failed to process form submission:', error.message);
      }

      if (shouldPreventDefault(form)) {
        event.preventDefault();
      }
    });
  }

  function processForms(doc) {
    try {
      Array.from(doc.getElementsByTagName('form')).forEach((form) => {
        handleFormSubmission(form);
      });
    } catch (error) {
      console.warn('Failed to process document forms:', error.message);
    }
  }

  function setupFormObserver(rootDocument) {
    if (typeof MutationObserver === 'undefined' || !rootDocument.body) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }

          if (node.tagName === 'FORM') {
            handleFormSubmission(node);
            return;
          }

          if (typeof node.querySelectorAll === 'function') {
            node.querySelectorAll('form').forEach((form) => {
              handleFormSubmission(form);
            });
          }
        });
      });
    });

    observer.observe(rootDocument.body, {
      childList: true,
      subtree: true
    });
  }

  let globalIframeManager = null;

  class IframeManager {
    constructor(leadData) {
      this.leadData = leadData;
      this.processedIframes = new WeakSet();
      this.populatedIframes = new WeakSet();
      this.observer = null;
      this.debounceTimer = null;
      this.initializeObserver();
    }

    initializeObserver() {
      if (typeof MutationObserver === 'undefined' || !document.body) {
        this.processExistingIframes();
        return;
      }

      this.observer = new MutationObserver((mutations) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.processMutations(mutations);
        }, 100);
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.processExistingIframes();
    }

    processMutations(mutations) {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.processNewElement(node);
          }
        });
      });
    }

    processNewElement(element) {
      if (element.tagName === 'IFRAME') {
        this.processIframe(element);
        return;
      }

      if (element.querySelectorAll) {
        element.querySelectorAll('iframe').forEach((iframe) => {
          this.processIframe(iframe);
        });
      }
    }

    processExistingIframes() {
      Array.from(document.getElementsByTagName('iframe')).forEach((iframe) => {
        this.processIframe(iframe);
      });
    }

    processIframe(iframe) {
      if (this.processedIframes.has(iframe)) {
        return;
      }

      this.processedIframes.add(iframe);

      iframe.addEventListener('load', () => {
        this.processLoadedIframe(iframe);
      });

      this.processLoadedIframe(iframe);
    }

    processLoadedIframe(iframe) {
      const readyStatus = this.isIframeReady(iframe);

      if (readyStatus === true) {
        this.populateAndAttachHandlers(iframe);
        return;
      }

      if (readyStatus === 'inaccessible') {
        this.attemptCrossOriginCommunication(iframe);
        return;
      }

      let attempts = 0;
      const maxAttempts = 50;
      const checkReady = () => {
        attempts += 1;
        const status = this.isIframeReady(iframe);

        if (status === true) {
          this.populateAndAttachHandlers(iframe);
        } else if (status === 'inaccessible') {
          this.attemptCrossOriginCommunication(iframe);
        } else if (attempts < maxAttempts) {
          setTimeout(checkReady, 100);
        }
      };

      setTimeout(checkReady, 50);
    }

    isIframeReady(iframe) {
      try {
        const doc = iframe.contentDocument;

        if (!doc) {
          return 'inaccessible';
        }

        return doc.readyState === 'complete' || doc.readyState === 'interactive';
      } catch (error) {
        return 'inaccessible';
      }
    }

    populateAndAttachHandlers(iframe) {
      if (this.populatedIframes.has(iframe)) {
        return;
      }

      this.populatedIframes.add(iframe);

      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        if (!doc) {
          return;
        }

        populateFormFields(doc, this.leadData);
        processForms(doc);
        setupFormObserver(doc);
      } catch (error) {
        this.attemptCrossOriginCommunication(iframe);
      }
    }

    attemptCrossOriginCommunication(iframe) {
      if (!iframe.contentWindow || !this.leadData || !this.leadData.parameters) {
        return;
      }

      try {
        iframe.contentWindow.postMessage(
          {
            type: 'utm_populate',
            source: 'utm_tracker',
            data: this.leadData.parameters,
            timestamp: Date.now()
          },
          '*'
        );
      } catch (error) {
        // Cross-origin messaging can fail for restricted frames.
      }
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
    }
  }

  function processIframes(leadData) {
    if (globalIframeManager) {
      globalIframeManager.destroy();
    }

    globalIframeManager = new IframeManager(leadData);
  }

  function handleCrossOriginFormSubmission(data) {
    if (!data.formData || typeof data.formData !== 'object') {
      return;
    }

    try {
      Object.entries(data.formData).forEach(([key, value]) => {
        pushToDataLayer({ [key]: value });
      });

      pushToDataLayer({
        event_id: Date.now().toString(),
        source: 'cross_origin_iframe'
      });
    } catch (error) {
      console.warn('Failed to process cross-origin form submission:', error.message);
    }
  }

  function handleCrossOriginIframeReady(source) {
    if (!globalIframeManager || !globalIframeManager.leadData || !source) {
      return;
    }

    try {
      source.postMessage(
        {
          type: 'utm_populate',
          source: 'utm_tracker',
          data: globalIframeManager.leadData.parameters,
          timestamp: Date.now()
        },
        '*'
      );
    } catch (error) {
      console.warn('Failed to respond to cross-origin iframe ready:', error.message);
    }
  }

  function setupCrossOriginMessageHandler() {
    window.addEventListener('message', function (event) {
      if (!event.data || typeof event.data !== 'object') {
        return;
      }

      switch (event.data.type) {
        case 'utm_form_submit':
          handleCrossOriginFormSubmission(event.data);
          break;
        case 'utm_ready':
          handleCrossOriginIframeReady(event.source);
          break;
        default:
          break;
      }
    });
  }

  function executeLeadAction(action) {
    switch (action.action) {
      case 'create':
      case 'replace':
        if (action.action === 'replace') {
          removeCookie(CONFIG.COOKIE_NAME);
        }

        saveLeadData(action.data);
        populateFormFields(document, action.data);
        processIframes(action.data);
        break;

      case 'use_existing':
        saveLeadData(action.data);
        populateFormFields(document, action.data);
        processIframes(action.data);
        break;

      default:
        break;
    }
  }

  function initializeLeadTracking() {
    try {
      setupCrossOriginMessageHandler();

      const urlParams = getUrlParameters();
      const existingLead = getExistingLeadData();
      const action = determineLeadAction(urlParams, existingLead);

      executeLeadAction(action);
      processForms(document);
      setupFormObserver(document);
    } catch (error) {
      console.error('Failed to initialize lead tracking:', error.message);
    }
  }

  window.addEventListener('load', initializeLeadTracking);
})();
